import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  UserGroupIcon,
  CalendarIcon,
  TrophyIcon,
  ChartBarIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  BellIcon
} from '@heroicons/react/24/outline'
import { useAuth0 } from '@auth0/auth0-react'
import { usersApi } from '../services/usersApi'
import { eventsApi } from '../services/eventsApi'
import PoolsApi from '../services/poolsApi'
import { isPoolAdmin } from '../utils/roles'
import { getPushSubscribers, sendAdminPush, type PushSubscriber } from '../utils/pushNotifications'
import toast from 'react-hot-toast'

interface AdminUser {
  userId: string
  email: string
  name: string
  role: 'user' | 'admin'
  status: 'pending' | 'approved' | 'denied'
  isApproved: boolean
  createdAt: string
}

interface Event {
  id: string
  title: string
  startDate: string
  endDate?: string
  location: string
  isMultiDay: boolean
  rsvpCount: number
  status: 'upcoming' | 'ongoing' | 'completed'
}

interface Pool {
  id: string
  name: string
  type: 'horse-racing' | 'football' | 'cornhole'
  status: 'open' | 'closed' | 'completed'
  participantCount: number
  totalPot?: number
}

interface AdminStats {
  totalUsers: number
  pendingUsers: number
  totalEvents: number
  activePools: number
  totalRevenue: number
}

export default function AdminPage() {
  const auth0 = useAuth0()
  const { user, getAccessTokenSilently } = auth0
  const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'events' | 'pools' | 'notifications'>('overview')
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState<AdminStats | null>(null)
  const [users, setUsers] = useState<AdminUser[]>([])
  const [events, setEvents] = useState<Event[]>([])
  const [pools, setPools] = useState<Pool[]>([])
  const [pushSubscribers, setPushSubscribers] = useState<PushSubscriber[]>([])
  const [pushStats, setPushStats] = useState({ totalUsers: 0, totalDevices: 0 })
  const [pushLoading, setPushLoading] = useState(false)
  const [selectedPushUserIds, setSelectedPushUserIds] = useState<string[]>([])
  const [pushMessage, setPushMessage] = useState('Push notifications are working!')
  const [pushTitle, setPushTitle] = useState('Midwest Railbirds')
  const [pushSending, setPushSending] = useState(false)

  useEffect(() => {
    usersApi.setAuth(getAccessTokenSilently)
    loadAdminData()
  }, [getAccessTokenSilently])

  const loadAdminData = async () => {
    try {
      setLoading(true)
      
      // Create poolsApi instance
      const poolsApiInstance = new PoolsApi(auth0)
      
      // Load all admin data in parallel
      const [statsData, usersData, eventsData, poolsData] = await Promise.all([
        usersApi.getAdminStats().catch(() => ({ totalUsers: 0, pendingUsers: 0, totalEvents: 0, activePools: 0, totalRevenue: 0 })),
        usersApi.getUsers().catch(() => ({ users: [], total: 0 })),
        eventsApi.getEvents().catch(() => ({ events: [], total: 0 })),
        poolsApiInstance.getPools().catch(() => ({ pools: [], total: 0 }))
      ])

      setStats(statsData)

      // Convert API users to AdminUser format
      const adminUsers: AdminUser[] = usersData.users.map(user => ({
        userId: user.userId,
        email: user.email,
        name: user.name || `${(user as any).firstName || ''} ${(user as any).lastName || ''}`.trim(),
        role: (user.role === 'admin' ? 'admin' : 'user') as 'user' | 'admin',
        status: (['pending', 'approved', 'denied'].includes((user as any).status)
          ? (user as any).status
          : ((user as any).status === 'active' ? 'approved' : 'pending')) as 'pending' | 'approved' | 'denied',
        isApproved: user.isApproved,
        createdAt: user.createdAt
      }))
      setUsers(adminUsers)

      // Convert API events to admin format
      const adminEvents = eventsData.events.map(event => ({
        id: event.eventId,
        title: event.title,
        startDate: event.startDate,
        endDate: event.endDate,
        location: event.location?.name || 'TBD',
        isMultiDay: event.isMultiDay,
        rsvpCount: event.rsvps.length,
        status: event.status
      }))
      setEvents(adminEvents)

      // Convert API pools to admin format
      const adminPools = poolsData.pools.map(pool => ({
        id: pool.poolId,
        name: pool.name,
        type: pool.type,
        status: pool.status,
        participantCount: pool.participantCount,
        totalPot: pool.prizePool
      }))
      setPools(adminPools)
      
    } catch (error) {
      toast.error('Failed to load admin data')
    } finally {
      setLoading(false)
    }
  }

  const handleUserAction = async (userId: string, action: 'approve' | 'deny' | 'delete') => {
    try {
      if (action === 'delete') {
        const confirmed = window.confirm(
          'Remove this user from the directory? Their record will be kept and can be recovered if they log in again (they will show as pending).'
        )
        if (!confirmed) return
        const result = await usersApi.deleteUser(userId)
        toast.success(result.note || 'User removed')
      } else if (action === 'approve') {
        const result = await usersApi.approveUser(userId)
        toast.success(result.note || 'User approved')
      } else if (action === 'deny') {
        await usersApi.denyUser(userId)
        toast.success('User denied')
      }

      await loadAdminData()
    } catch (error: any) {
      toast.error(error?.message || `Failed to ${action} user`)
    }
  }

  const loadPushSubscribers = async () => {
    try {
      setPushLoading(true)
      const data = await getPushSubscribers(getAccessTokenSilently)
      const subscribers = data.subscribers || []
      setPushSubscribers(subscribers)
      setPushStats({
        totalUsers: data.totalUsers || 0,
        totalDevices: data.totalDevices || 0,
      })
      setSelectedPushUserIds((prev) => prev.filter((id) => subscribers.some((s) => s.userId === id)))
    } catch (error: any) {
      toast.error(error?.message || 'Failed to load push subscribers')
      setPushSubscribers([])
      setPushStats({ totalUsers: 0, totalDevices: 0 })
      setSelectedPushUserIds([])
    } finally {
      setPushLoading(false)
    }
  }

  const allPushSelected =
    pushSubscribers.length > 0 && selectedPushUserIds.length === pushSubscribers.length

  const toggleSelectAllPush = () => {
    if (allPushSelected) {
      setSelectedPushUserIds([])
    } else {
      setSelectedPushUserIds(pushSubscribers.map((s) => s.userId))
    }
  }

  const togglePushUser = (userId: string) => {
    setSelectedPushUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    )
  }

  const handleSendPushToSelected = async () => {
    if (selectedPushUserIds.length === 0) {
      toast.error('Select at least one member')
      return
    }
    if (!pushMessage.trim()) {
      toast.error('Enter a message')
      return
    }

    setPushSending(true)
    try {
      const result = await sendAdminPush(getAccessTokenSilently, {
        userIds: selectedPushUserIds,
        title: pushTitle.trim() || 'Midwest Railbirds',
        body: pushMessage.trim(),
      })
      toast.success(`Sent to ${result.recipients} member${result.recipients === 1 ? '' : 's'} (${result.sent} device${result.sent === 1 ? '' : 's'})`)
    } catch (error: any) {
      toast.error(error?.message || 'Failed to send')
    } finally {
      setPushSending(false)
    }
  }

  useEffect(() => {
    if (activeTab === 'notifications') {
      loadPushSubscribers()
    }
  }, [activeTab, getAccessTokenSilently])

  const getStatusBadge = (status: string) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
    switch (status) {
      case 'pending':
        return `${baseClasses} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`
      case 'approved':
      case 'open':
      case 'upcoming':
        return `${baseClasses} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`
      case 'denied':
      case 'closed':
        return `${baseClasses} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`
      case 'completed':
        return `${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200`
      default:
        return `${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200`
    }
  }

  // Admin access is now protected at the route level via AdminRoute component

  const tabs = [
    { id: 'overview', name: 'Overview', icon: ChartBarIcon },
    { id: 'users', name: 'Users', icon: UserGroupIcon },
    { id: 'events', name: 'Events', icon: CalendarIcon },
    { id: 'pools', name: 'Pools', icon: TrophyIcon },
    { id: 'notifications', name: 'Notifications', icon: BellIcon },
  ]

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Admin Dashboard</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            Manage users, events, pools, and view analytics
          </p>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.name}</span>
              </button>
            ))}
          </nav>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && stats && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex items-center">
                      <UserGroupIcon className="w-8 h-8 text-blue-600" />
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Total Users</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalUsers}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex items-center">
                      <ClockIcon className="w-8 h-8 text-yellow-600" />
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Pending Users</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.pendingUsers}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex items-center">
                      <CalendarIcon className="w-8 h-8 text-green-600" />
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Total Events</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalEvents}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex items-center">
                      <TrophyIcon className="w-8 h-8 text-purple-600" />
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Active Pools</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.activePools}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex items-center">
                      <ChartBarIcon className="w-8 h-8 text-primary-600" />
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Total Revenue</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">${stats.totalRevenue}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Actions for Pool Admins */}
                {isPoolAdmin(user) && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Pool Administration</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <a
                        href="/admin/pools/football"
                        className="flex items-center p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
                      >
                        <TrophyIcon className="w-8 h-8 text-green-600 dark:text-green-400 mr-4" />
                        <div>
                          <h4 className="font-medium text-green-900 dark:text-green-100">Football Pools</h4>
                          <p className="text-sm text-green-700 dark:text-green-300">Manage football pool games and picks</p>
                        </div>
                      </a>
                      <a
                        href="/admin/pools/horse-racing"
                        className="flex items-center p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg hover:bg-yellow-100 dark:hover:bg-yellow-900/30 transition-colors"
                      >
                        <div className="w-8 h-8 text-2xl mr-4">🐎</div>
                        <div>
                          <h4 className="font-medium text-yellow-900 dark:text-yellow-100">Horse Racing Pools</h4>
                          <p className="text-sm text-yellow-700 dark:text-yellow-300">Manage horse racing pools and races</p>
                        </div>
                      </a>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white">User Management</h2>
                  <button className="btn-primary flex items-center">
                    <PlusIcon className="w-4 h-4 mr-2" />
                    Add User
                  </button>
                </div>
                
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          User
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Role
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Joined
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {users.map((user) => (
                        <tr key={user.userId}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900 dark:text-white">{user.name}</div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">{user.email}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-900 dark:text-white capitalize">{user.role}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={getStatusBadge(user.status)}>
                              {user.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {new Date(user.createdAt).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex justify-end space-x-2">
                              {user.status === 'pending' && (
                                <>
                                  <button
                                    onClick={() => handleUserAction(user.userId, 'approve')}
                                    className="text-green-600 hover:text-green-900"
                                    title="Approve"
                                  >
                                    <CheckCircleIcon className="w-5 h-5" />
                                  </button>
                                  <button
                                    onClick={() => handleUserAction(user.userId, 'deny')}
                                    className="text-red-600 hover:text-red-900"
                                    title="Deny"
                                  >
                                    <XCircleIcon className="w-5 h-5" />
                                  </button>
                                </>
                              )}
                              <button
                                className="text-primary-600 hover:text-primary-900"
                                title="Edit"
                              >
                                <PencilIcon className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleUserAction(user.userId, 'delete')}
                                className="text-red-600 hover:text-red-900"
                                title="Delete"
                              >
                                <TrashIcon className="w-5 h-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Events Tab */}
            {activeTab === 'events' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white">Event Management</h2>
                  <button className="btn-primary flex items-center">
                    <PlusIcon className="w-4 h-4 mr-2" />
                    Create Event
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {events.map((event) => (
                    <div key={event.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
                      <div className="flex justify-between items-start mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{event.title}</h3>
                        <span className={getStatusBadge(event.status)}>
                          {event.status}
                        </span>
                      </div>
                      <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300 mb-4">
                        <p>📅 {event.startDate}{event.endDate && ` - ${event.endDate}`}</p>
                        <p>📍 {event.location}</p>
                        <p>👥 {event.rsvpCount} attendees</p>
                      </div>
                      <div className="flex justify-end space-x-2">
                        <button className="text-primary-600 hover:text-primary-900" title="Edit">
                          <PencilIcon className="w-5 h-5" />
                        </button>
                        <button className="text-red-600 hover:text-red-900" title="Delete">
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pools Tab */}
            {activeTab === 'pools' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white">Pool Overview</h2>
                  {isPoolAdmin(user) && (
                    <div className="flex space-x-3">
                      <a 
                        href="/admin/pools/football" 
                        className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                      >
                        <TrophyIcon className="w-4 h-4 mr-2" />
                        Football Pools
                      </a>
                      <a 
                        href="/admin/pools/horse-racing" 
                        className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                      >
                        <span className="w-4 h-4 mr-2">🐎</span>
                        Horse Racing Pools
                      </a>
                    </div>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {pools.map((pool) => (
                    <div key={pool.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
                      <div className="flex justify-between items-start mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{pool.name}</h3>
                        <span className={getStatusBadge(pool.status)}>
                          {pool.status}
                        </span>
                      </div>
                      <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300 mb-4">
                        <p>🎯 {pool.type.replace('-', ' ')}</p>
                        <p>👥 {pool.participantCount} participants</p>
                        {pool.totalPot && <p>💰 ${pool.totalPot} total pot</p>}
                      </div>
                      {isPoolAdmin(user) && (
                        <div className="flex justify-end space-x-2">
                          <button className="text-primary-600 hover:text-primary-900" title="Edit">
                            <PencilIcon className="w-5 h-5" />
                          </button>
                          <button className="text-red-600 hover:text-red-900" title="Delete">
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-lg font-medium text-gray-900 dark:text-white">Push Notifications</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                      Select members, write a message, then send
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={loadPushSubscribers}
                    disabled={pushLoading}
                    className="btn-secondary text-sm disabled:opacity-50"
                  >
                    {pushLoading ? 'Refreshing…' : 'Refresh'}
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-4">
                    <p className="text-sm text-gray-600 dark:text-gray-300">Members with push</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{pushStats.totalUsers}</p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-4">
                    <p className="text-sm text-gray-600 dark:text-gray-300">Registered devices</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{pushStats.totalDevices}</p>
                  </div>
                </div>

                {pushSubscribers.length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-4 space-y-3">
                    <div>
                      <label htmlFor="push-title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Title
                      </label>
                      <input
                        id="push-title"
                        type="text"
                        value={pushTitle}
                        onChange={(e) => setPushTitle(e.target.value)}
                        maxLength={80}
                        className="block w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm"
                      />
                    </div>
                    <div>
                      <label htmlFor="push-message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Message
                      </label>
                      <textarea
                        id="push-message"
                        rows={3}
                        value={pushMessage}
                        onChange={(e) => setPushMessage(e.target.value)}
                        maxLength={200}
                        placeholder="Type the notification message…"
                        className="block w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm resize-y"
                      />
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      <button
                        type="button"
                        onClick={handleSendPushToSelected}
                        disabled={pushSending || selectedPushUserIds.length === 0 || !pushMessage.trim()}
                        className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {pushSending
                          ? 'Sending…'
                          : `Send to selected (${selectedPushUserIds.length})`}
                      </button>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {selectedPushUserIds.length === 0
                          ? 'Check one or more members below'
                          : `${selectedPushUserIds.length} selected`}
                      </span>
                    </div>
                  </div>
                )}

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
                  {pushLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                    </div>
                  ) : pushSubscribers.length === 0 ? (
                    <div className="px-6 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                      No one has enabled push notifications yet.
                    </div>
                  ) : (
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-4 py-3 text-left w-12">
                            <input
                              type="checkbox"
                              checked={allPushSelected}
                              onChange={toggleSelectAllPush}
                              aria-label="Select all"
                              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Member
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Devices
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            First enabled
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {pushSubscribers.map((subscriber) => {
                          const firstEnabled = subscriber.devices
                            .map((d) => d.createdAt)
                            .filter(Boolean)
                            .sort()[0]
                          const deviceSummary = [...new Set(subscriber.devices.map((d) => d.device))].join(', ')
                          const checked = selectedPushUserIds.includes(subscriber.userId)
                          return (
                            <tr
                              key={subscriber.userId}
                              className={checked ? 'bg-primary-50/50 dark:bg-primary-900/10' : undefined}
                            >
                              <td className="px-4 py-4">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => togglePushUser(subscriber.userId)}
                                  aria-label={`Select ${subscriber.name}`}
                                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                />
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">{subscriber.name}</div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">{subscriber.email}</div>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                {subscriber.deviceCount} ({deviceSummary || 'Unknown'})
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                {firstEnabled ? new Date(firstEnabled).toLocaleString() : '—'}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </motion.div>
    </div>
  )
}
